import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import type { TFunction } from 'i18next';
import { Button } from '@/components/Button';
import { PhoneField } from '@/components/PhoneField/PhoneField';
import { TextField } from '@/components/TextField';
import { Textarea } from '@/components/Textarea';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { Skeleton } from '@/components/Skeleton';
import { postContactForm } from '@/lib/contact-api';
import { useRecaptchaToken } from '@/lib/useRecaptchaToken';
import { findSection } from '@/lib/cms/section-types';
import { getCmsDocument, type CmsPageLocale } from '@/lib/db/cms-pages';
import { CONTACT_META } from '@/lib/seo/site';
import { useSeoHead } from '@/lib/seo/useSeoHead';

const SUBJECTS = ['inquiry', 'partnership', 'press', 'support'] as const;
type Subject = (typeof SUBJECTS)[number];

function contactSchema(t: TFunction) {
  return z.object({
    fullName: z.string().trim().min(1, t('contact.form.validation.fullNameRequired')),
    email: z
      .string()
      .trim()
      .toLowerCase()
      .min(1, t('contact.form.validation.emailRequired'))
      .email(t('contact.form.validation.emailInvalid')),
    phone: z.string().trim().optional(),
    message: z
      .string()
      .trim()
      .min(1, t('contact.form.validation.messageRequired'))
      .min(20, t('contact.form.validation.messageMin'))
      .max(2_000, t('contact.form.validation.messageMax')),
  });
}
type ContactValues = z.infer<ReturnType<typeof contactSchema>>;

export function ContactPage() {
  const { t, i18n } = useTranslation();
  useSeoHead(CONTACT_META);
  const [subject, setSubject] = useState<Subject>('inquiry');
  const [sent, setSent] = useState(false);
  const [cmsTitle, setCmsTitle] = useState<string | null>(null);
  const [cmsLead, setCmsLead] = useState<string | null>(null);
  const [cmsLoading, setCmsLoading] = useState(true);

  const cmsLocale = (
    ['en', 'fr', 'ar'].includes(i18n.language?.split('-')[0] ?? '')
      ? (i18n.language.split('-')[0] as CmsPageLocale)
      : 'en'
  ) as CmsPageLocale;

  // CMS copy is strict: loading / absent / hidden / emptied → not rendered,
  // no i18n fallback.
  useEffect(() => {
    setCmsLoading(true);
    getCmsDocument('contact', cmsLocale)
      .then((doc) => {
        const intro = findSection(doc, 'rich_text');
        setCmsTitle(intro && intro.visible ? intro.title || null : null);
        setCmsLead(intro && intro.visible ? intro.html || null : null);
      })
      .catch(() => {})
      .finally(() => setCmsLoading(false));
  }, [cmsLocale]);
  const getRecaptchaToken = useRecaptchaToken();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    control,
  } = useForm<ContactValues>({
    resolver: zodResolver(contactSchema(t)),
    defaultValues: { fullName: '', email: '', phone: '', message: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    let token = '';
    try {
      token = (await getRecaptchaToken('contact')) ?? '';
    } catch {
      toast.error(t('contact.form.errors.generic'));
      return;
    }

    const localeBase = (i18n.language ?? 'en').split('-')[0]?.toLowerCase();
    const locale = localeBase === 'fr' ? 'fr' : localeBase === 'ar' ? 'ar' : 'en';

    const result = await postContactForm({
      fullName: values.fullName,
      email: values.email,
      phone: values.phone,
      subject,
      message: values.message,
      recaptchaToken: token,
      locale,
    });

    if (result.ok) {
      setSent(true);
      return;
    }

    switch (result.code) {
      case 'rate_limited':
        toast.error(t('contact.form.errors.rateLimited'));
        break;
      case 'recaptcha_failed':
        break;
      default:
        toast.error(t('contact.form.errors.generic'));
        break;
    }
  });

  const restart = () => {
    reset();
    setSent(false);
    setSubject('inquiry');
  };

  return (
    <div className="public">
      <div className="contact-shell">
        <div className="contact-shell__head">
          <div className="contact-shell__kicker">{t('contact.kicker')}</div>
          {cmsLoading ? (
            <>
              <Skeleton width="50%" height={36} style={{ margin: '8px 0 16px' }} />
              <Skeleton width="78%" height={14} />
            </>
          ) : (
            <>
              {cmsTitle && <h1 className="contact-shell__title">{cmsTitle}</h1>}
              {cmsLead && (
                <div
                  className="contact-shell__lead"
                  dangerouslySetInnerHTML={{ __html: cmsLead }}
                />
              )}
            </>
          )}
        </div>
        <div className="contact-shell__grid">
          <div className="contact-card">
            {sent ? (
              <div className="contact-card__success">
                <div className="contact-card__success-title">{t('contact.form.successTitle')}</div>
                <div className="contact-card__success-lead">{t('contact.form.successLead')}</div>
                <button type="button" className="contact-card__success-link" onClick={restart}>
                  {t('actions.sendAnother')}
                </button>
              </div>
            ) : (
              <form className="m5-form" onSubmit={onSubmit} noValidate>
                <div className="contact-form__row">
                  <TextField
                    label={t('contact.form.fullName')}
                    placeholder={t('contact.form.fullNamePlaceholder')}
                    autoComplete="name"
                    required
                    error={errors.fullName?.message}
                    {...register('fullName')}
                  />
                  <TextField
                    label={t('contact.form.email')}
                    type="email"
                    placeholder={t('contact.form.emailPlaceholder')}
                    autoComplete="email"
                    inputMode="email"
                    required
                    error={errors.email?.message}
                    {...register('email')}
                  />
                </div>
                <Controller
                  control={control}
                  name="phone"
                  render={({ field, fieldState }) => (
                    <PhoneField
                      name="phone"
                      label={t('contact.form.phone')}
                      placeholder={t('contact.form.phonePlaceholder')}
                      helper={t('contact.form.phoneHint')}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      error={fieldState.error?.message}
                    />
                  )}
                />
                <div className="contact-form__chips">
                  <div className="contact-form__chips-label">{t('contact.form.subject')}</div>
                  <div className="contact-form__chips-row" role="group">
                    {SUBJECTS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={`contact-form__chip${subject === s ? ' is-active' : ''}`}
                        aria-pressed={subject === s}
                        onClick={() => setSubject(s)}
                      >
                        {t(`contact.form.subjects.${s}`)}
                      </button>
                    ))}
                  </div>
                </div>
                <Textarea
                  label={t('contact.form.message')}
                  placeholder={t('contact.form.messagePlaceholder')}
                  rows={6}
                  required
                  error={errors.message?.message}
                  {...register('message')}
                />
                <div className="contact-form__footer">
                  <span className="contact-form__notice">{t('contact.form.recaptchaNotice')}</span>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={isSubmitting}
                    loadingLabel="…"
                    className="contact-form__submit"
                  >
                    {t('actions.send')}
                  </Button>
                </div>
              </form>
            )}
          </div>
          <aside className="contact-aside">
            <div className="contact-aside__card">
              <div className="contact-aside__kicker">{t('contact.details.kicker')}</div>
              {(['office', 'hours', 'email'] as const).map((key) => (
                <div key={key} className="contact-aside__row">
                  <div className="contact-aside__label">{t(`contact.details.${key}.label`)}</div>
                  <div className="contact-aside__value">
                    {key === 'email' ? (
                      <a href={`mailto:${t('contact.details.email.value')}`}>
                        {t('contact.details.email.value')}
                      </a>
                    ) : (
                      t(`contact.details.${key}.value`)
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="contact-aside__card contact-aside__card--dark">
              <div className="contact-aside__kicker">{t('contact.whatsappCard.kicker')}</div>
              <div className="contact-aside__title-dark">{t('contact.whatsappCard.title')}</div>
              <div className="contact-aside__phone-dark">{t('contact.whatsappCard.phone')}</div>
              <WhatsAppButton label={t('actions.openWhatsapp')} size="md" fullWidth />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
